import React, { useState, useEffect } from 'react';
import { X, Clock, Users, MapPin, Briefcase, CheckCircle, XCircle, UserPlus, AlertTriangle } from 'lucide-react';
import { jobAPI } from '../../../services';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const JobDetailModal = ({ job, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Determine if user can approve (admin or hr_head)
  const canApprove = ['admin', 'hr_head', 'superadmin'].includes(user?.role);
  
  // Debug logging
  console.log('JobDetailModal - user role:', user?.role, 'canApprove:', canApprove);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showExtendForm, setShowExtendForm] = useState(false);
  const [taTeam, setTATeam] = useState([]);
  const [selectedTA, setSelectedTA] = useState('');
  const [extensionDays, setExtensionDays] = useState(7);
  const [extensionReason, setExtensionReason] = useState('');
  const [jobDetails, setJobDetails] = useState(null);
  const [candidateStats, setCandidateStats] = useState(null);

  useEffect(() => {
    if (job?._id) {
      fetchJobDetails();
      // Always fetch TA team for admins who can approve
      if (canApprove) {
        fetchTATeam();
      }
    }
  }, [job?._id, canApprove]);

  const fetchJobDetails = async () => {
    if (!job?._id) return;
    try {
      const response = await jobAPI.getJob(job._id);
      if (response.status === 'success') {
        setJobDetails(response.data.job);
        setCandidateStats(response.data.candidateStats);
      }
    } catch (err) {
      console.error('Failed to fetch job details:', err);
    }
  };

  const fetchTATeam = async () => {
    try {
      console.log('Fetching TA team...');
      const response = await jobAPI.getTATeam();
      console.log('TA team response:', response);
      if (response.status === 'success') {
        setTATeam(response.data.taTeam || []);
      }
    } catch (err) {
      console.error('Failed to fetch TA team:', err);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      setError('');
      
      await jobAPI.approveJob(job._id, {
        action: 'approve',
        notes: actionNotes,
        assignTo: selectedTA || undefined
      });
      
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to approve job');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!actionNotes.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await jobAPI.approveJob(job._id, {
        action: 'reject',
        notes: actionNotes
      });
      
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to reject job');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTA) {
      setError('Please select a TA Executive');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await jobAPI.assignJob(job._id, { assignTo: selectedTA });
      
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to assign job');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestExtension = async () => {
    if (!extensionReason.trim()) {
      setError('Please provide a reason for extension');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await jobAPI.requestTATExtension(job._id, {
        extensionDays,
        reason: extensionReason
      });
      
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to request extension');
    } finally {
      setLoading(false);
    }
  };

  const currentJob = jobDetails || job;

  // Early return if no job data
  if (!currentJob) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl p-6">
          <p className="text-gray-500">Loading job details...</p>
        </div>
      </div>
    );
  }

  // Debug logging for status
  console.log('JobDetailModal - job status:', currentJob.status, 'isPending:', currentJob.status?.toLowerCase() === 'pending');

  const isPending = currentJob.status?.toLowerCase() === 'pending';
  const isApproved = currentJob.status?.toLowerCase() === 'approved';
  const isRejected = currentJob.status?.toLowerCase() === 'rejected';

  const getTATStatus = () => {
    if (!currentJob.deadline || currentJob.status !== 'approved') return null;
    
    const now = new Date();
    const deadline = new Date(currentJob.deadline);
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { label: `${Math.abs(daysLeft)} days overdue`, color: 'text-red-600', bgColor: 'bg-red-50' };
    } else if (daysLeft <= 3) {
      return { label: `${daysLeft} days left`, color: 'text-red-600', bgColor: 'bg-red-50' };
    } else if (daysLeft <= 7) {
      return { label: `${daysLeft} days left`, color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    }
    return { label: `${daysLeft} days left`, color: 'text-green-600', bgColor: 'bg-green-50' };
  };

  const tatStatus = getTATStatus();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{currentJob.title}</h2>
            <p className="text-gray-500 mt-1">{currentJob.department} • {currentJob.designation}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
              isApproved ? 'bg-green-100 text-green-700' :
              isPending ? 'bg-yellow-100 text-yellow-700' :
              isRejected ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {currentJob.status?.toUpperCase().replace('_', ' ')}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              {error}
            </div>
          )}

          {/* TAT Status */}
          {tatStatus && (
            <div className={`flex items-center gap-3 p-4 rounded-lg ${tatStatus.bgColor}`}>
              <Clock className={`w-5 h-5 ${tatStatus.color}`} />
              <div>
                <span className={`font-semibold ${tatStatus.color}`}>{tatStatus.label}</span>
                <span className="text-gray-600 ml-2">
                  (Deadline: {new Date(currentJob.deadline).toLocaleDateString()})
                </span>
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Positions</span>
              </div>
              <span className="text-xl font-bold text-gray-900">{currentJob.positions}</span>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm">Experience</span>
              </div>
              <span className="text-xl font-bold text-gray-900">
                {currentJob.experience?.min || 0} - {currentJob.experience?.max || 0} yrs
              </span>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Location</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {currentJob.location || 'Not specified'}
              </span>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">TAT</span>
              </div>
              <span className="text-xl font-bold text-gray-900">{currentJob.tatDays} days</span>
            </div>
          </div>

          {/* Request Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Request Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Requested By (HOD):</span>
                <span className="font-medium">{currentJob.requestedBy?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Requested On:</span>
                <span className="font-medium">{new Date(currentJob.requestedAt || currentJob.createdAt).toLocaleDateString()}</span>
              </div>
              {currentJob.approvedBy && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Approved By (HR Head):</span>
                    <span className="font-medium">{currentJob.approvedBy?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Approved On:</span>
                    <span className="font-medium">{new Date(currentJob.approvedAt).toLocaleDateString()}</span>
                  </div>
                </>
              )}
              {currentJob.assignedTo && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Assigned To:</span>
                  <span className="font-medium">{currentJob.assignedTo?.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Job Description</h4>
            <p className="text-gray-700 whitespace-pre-line">{currentJob.description}</p>
          </div>

          {/* Skills */}
          {currentJob.skills?.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Required Skills</h4>
              <div className="flex flex-wrap gap-2">
                {currentJob.skills.map((skill, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Qualifications */}
          {currentJob.qualifications?.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Qualifications</h4>
              <ul className="list-disc list-inside text-gray-700">
                {currentJob.qualifications.map((qual, index) => (
                  <li key={index}>{qual}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Candidate Stats */}
          {candidateStats && isApproved && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Candidate Pipeline</h4>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {Object.entries(candidateStats).filter(([key]) => key !== 'total').map(([stage, count]) => (
                  <div key={stage} className="text-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-lg font-bold text-gray-900">{count}</span>
                    <p className="text-xs text-gray-500 capitalize">{stage.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
              
              {candidateStats.total > 0 && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/dashboard/recruitment/pipeline/${currentJob._id}`);
                  }}
                  className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View Pipeline →
                </button>
              )}
            </div>
          )}

          {/* Rejection Reason */}
          {isRejected && currentJob.rejectionReason && (
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Rejection Reason</h4>
              <p className="text-red-700">{currentJob.rejectionReason}</p>
            </div>
          )}

          {/* Action Forms */}
          {canApprove && isPending && !showApproveForm && !showRejectForm && (
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowApproveForm(true)}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <CheckCircle className="w-5 h-5" />
                Approve
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                className="flex items-center gap-2 px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
              >
                <XCircle className="w-5 h-5" />
                Reject
              </button>
            </div>
          )}

          {/* Approve Form */}
          {showApproveForm && (
            <div className="bg-green-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-green-800">Approve Job Request</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to Team Member (Optional)
                </label>
                <select
                  value={selectedTA}
                  onChange={(e) => setSelectedTA(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Select Team Member</option>
                  {taTeam.map(ta => (
                    <option key={ta._id} value={ta._id}>
                      {ta.name} {ta.employeeId ? `(${ta.employeeId})` : ''} - {ta.role}
                    </option>
                  ))}
                </select>
                {taTeam.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No team members available</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Approval Notes (Optional)
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Any notes for the team..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Confirm Approval'}
                </button>
                <button
                  onClick={() => setShowApproveForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Reject Form */}
          {showRejectForm && (
            <div className="bg-red-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-red-800">Reject Job Request</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={loading}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Assign TA (for approved jobs without assignment) */}
          {canApprove && isApproved && !currentJob.assignedTo && !showAssignForm && (
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowAssignForm(true)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-5 h-5" />
                Assign TA Executive
              </button>
            </div>
          )}

          {showAssignForm && (
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-blue-800">Assign TA Executive</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select TA Executive <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedTA}
                  onChange={(e) => setSelectedTA(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Select TA Executive</option>
                  {taTeam.map(ta => (
                    <option key={ta._id} value={ta._id}>{ta.name} ({ta.employeeId})</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleAssign}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Assigning...' : 'Assign'}
                </button>
                <button
                  onClick={() => setShowAssignForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* TAT Extension (for approved jobs) */}
          {isApproved && !showExtendForm && (
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowExtendForm(true)}
                className="flex items-center gap-2 px-6 py-2 border border-yellow-600 text-yellow-700 rounded-lg hover:bg-yellow-50"
              >
                <AlertTriangle className="w-5 h-5" />
                Request TAT Extension
              </button>
            </div>
          )}

          {showExtendForm && (
            <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-yellow-800">Request TAT Extension</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Extension Days <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value) || 7)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={extensionReason}
                  onChange={(e) => setExtensionReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Please provide a reason for the extension..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleRequestExtension}
                  disabled={loading}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  {loading ? 'Requesting...' : 'Submit Request'}
                </button>
                <button
                  onClick={() => setShowExtendForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;
